
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Download, Search, Calendar, ExternalLink } from 'lucide-react';
import { getUserReferralClicks } from '@/utils/referral/referralTrackingService';
import { getCurrentUser } from '@/utils/auth';
import { ReferralClick } from '@/utils/referral/types';

const ReferralLinkTrackingReport = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [clicks, setClicks] = useState<ReferralClick[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredClicks, setFilteredClicks] = useState<ReferralClick[]>([]);
  
  useEffect(() => {
    const loadReferralClicks = async () => {
      setIsLoading(true);
      try {
        const currentUser = getCurrentUser();
        if (!currentUser || !currentUser.id) {
          console.error('No user found or missing user ID');
          setIsLoading(false);
          return;
        }
        
        const referralClicks = await getUserReferralClicks(currentUser.id);
        setClicks(referralClicks);
        setFilteredClicks(referralClicks);
      } catch (error) {
        console.error('Error loading referral clicks:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadReferralClicks();
  }, []);
  
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredClicks(clicks);
      return;
    }
    
    const lowercaseQuery = searchQuery.toLowerCase();
    const filtered = clicks.filter(click => 
      click.referralCode.toLowerCase().includes(lowercaseQuery) ||
      (click.origin || '').toLowerCase().includes(lowercaseQuery) ||
      (click.landingPage || '').toLowerCase().includes(lowercaseQuery) ||
      (click.visitorInfo?.name || '').toLowerCase().includes(lowercaseQuery) ||
      (click.visitorInfo?.email || '').toLowerCase().includes(lowercaseQuery)
    );
    
    setFilteredClicks(filtered);
  }, [searchQuery, clicks]);
  
  const handleExportCSV = () => {
    // Convert the clicks data to CSV format
    const headers = ['Referral Code', 'Origin', 'Landing Page', 'Click Date', 'Visitor Name', 'Visitor Email', 'Converted', 'Signup Date'];
    const csvData = filteredClicks.map(click => [
      click.referralCode,
      click.origin || '',
      click.landingPage || '',
      new Date(click.clickDate || click.timestamp).toLocaleString(),
      click.visitorInfo?.name || '',
      click.visitorInfo?.email || '',
      click.visitorInfo?.converted ? 'Yes' : 'No',
      click.visitorInfo?.signupDate ? new Date(click.visitorInfo.signupDate).toLocaleDateString() : ''
    ]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create a blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `referral_clicks_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ExternalLink className="h-5 w-5" />
          Referral Link Tracking Report
        </CardTitle>
        <CardDescription>
          Track which referral links were clicked and by whom
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <div className="relative w-full sm:w-auto flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="search"
              placeholder="Search by referral code, origin, or visitor..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-2"
              onClick={handleExportCSV}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>
        
        {filteredClicks.length > 0 ? (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date & Time
                    </div>
                  </TableHead>
                  <TableHead>Referral Code</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead>Landing Page</TableHead>
                  <TableHead>Visitor</TableHead>
                  <TableHead>Converted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClicks.map((click) => (
                  <TableRow key={click.id}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(click.clickDate || click.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono font-medium">
                      {click.referralCode}
                    </TableCell>
                    <TableCell>
                      {click.origin === 'direct' ? (
                        <span className="text-gray-500">Direct / No Referrer</span>
                      ) : (
                        <span className="text-blue-600 truncate max-w-[200px] block">
                          {click.origin || 'Unknown'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-blue-600 truncate max-w-[200px] block">
                        {click.landingPage || 'Homepage'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {click.visitorInfo?.name ? (
                        <div>
                          <p className="font-medium">{click.visitorInfo.name}</p>
                          {click.visitorInfo.email && (
                            <p className="text-xs text-gray-500">{click.visitorInfo.email}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">Anonymous</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {click.visitorInfo?.converted ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Converted
                          {click.visitorInfo.signupDate && (
                            <span className="ml-1">
                              ({new Date(click.visitorInfo.signupDate).toLocaleDateString()})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Not Converted
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No referral link clicks found</p>
            {searchQuery && (
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search criteria</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralLinkTrackingReport;
